import { ExtractComponentProp, FormValues } from '@approvalmax/types';
import { DatePicker, Form, Grid, Select, Spacing, Text } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { useSelector } from 'modules/react-redux';
import { memo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNetSuiteInvoiceFieldSettings } from 'shared/hooks';
import { z } from 'zod';

import { getNetSuiteContext } from '../../../selectors/netsuite/netSuiteSelectors';
import { useNetSuiteInvoiceFields } from '../NetSuiteInvoiceRequestForm/NetSuiteInvoiceRequestForm.hooks';
import { netSuiteInvoiceFieldValuesState } from '../NetSuiteInvoiceRequestForm/NetSuiteInvoiceRequestForm.states';
import { isFieldMandatory, isFieldReadOnly, isFieldVisible } from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteShowErrorsState } from '../resources/states';
import { useShippingAddresses, useShippingMethods } from './InvoiceShippingDetailsSection.hooks';
import { messages } from './InvoiceShippingDetailsSection.messages';
import { createSchema } from './InvoiceShippingDetailsSection.schema';

export const InvoiceShippingDetailsSection = memo(() => {
    const { documentFields } = useSelector(getNetSuiteContext);

    const showErrors = useAtomValue(netSuiteShowErrorsState);
    const fieldValues = useAtomValue(netSuiteInvoiceFieldValuesState);

    const { shippingAddressesItems, isFetchingShippingAddresses } = useShippingAddresses();
    const { shippingMethodsItems, isFetchingShippingMethods, handleSearchShippingMethod } = useShippingMethods();
    const { onFieldChange } = useNetSuiteInvoiceFields();
    const fieldSettings = useNetSuiteInvoiceFieldSettings(documentFields);

    const isShippingDateVisible = isFieldVisible(fieldSettings.shippingDate);
    const isShippingMethodVisible = isFieldVisible(fieldSettings.shippingMethod);
    const isShippingAddressVisible = isFieldVisible(fieldSettings.shippingAddress);

    const schema = createSchema(fieldSettings);

    const form = useForm<z.infer<typeof schema>>({
        values: {
            shipDate: fieldValues.shipDate || null,
            shipMethod: fieldValues.shipMethod?.id || null,
            shippingAddress: fieldValues.shippingAddress?.id || null,
        },
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const handleFormChange: ExtractComponentProp<typeof Form<FormValues<typeof form>>, 'onChange'> = (
        values,
        rootName
    ) => {
        switch (rootName) {
            case 'shipMethod':
                onFieldChange(rootName, shippingMethodsItems.find(({ id }) => id === values[rootName]) || null);
                break;
            case 'shippingAddress':
                onFieldChange(rootName, shippingAddressesItems.find(({ id }) => id === values[rootName]) || null);
                break;
            default:
                onFieldChange(rootName, values[rootName]);
                break;
        }
    };

    useEffect(() => {
        if (!showErrors) return;

        form.trigger();
    }, [form, showErrors]);

    if (!isShippingDateVisible && !isShippingMethodVisible && !isShippingAddressVisible) return null;

    return (
        <Form form={form} onChange={handleFormChange}>
            <Text font='headline' fontSize='xxsmall' fontWeight='medium' as='h2'>
                {messages.title}
            </Text>

            <Spacing height={16} />

            <Grid gridTemplateColumns='repeat(2, 1fr)'>
                <Grid gap={16} gridTemplateColumns='repeat(3, 1fr)'>
                    {isShippingDateVisible && (
                        <DatePicker
                            name='shipDate'
                            label={messages.shippingDateLabel}
                            placeholder={messages.shippingDatePlaceholder}
                            required={isFieldMandatory(fieldSettings.shippingDate)}
                            disabled={isFieldReadOnly(fieldSettings.shippingDate)}
                            size='small'
                        />
                    )}

                    {isShippingMethodVisible && (
                        <Select
                            name='shipMethod'
                            label={messages.shippingMethodLabel}
                            placeholder={messages.shippingMethodPlaceholder}
                            required={isFieldMandatory(fieldSettings.shippingMethod)}
                            disabled={isFieldReadOnly(fieldSettings.shippingMethod)}
                            items={shippingMethodsItems}
                            autocomplete
                            onInputChange={handleSearchShippingMethod}
                            itemNameKey='text'
                            size='small'
                            progress={isFetchingShippingMethods}
                        />
                    )}

                    {isShippingAddressVisible && (
                        <Select
                            name='shippingAddress'
                            label={messages.shippingAddressLabel}
                            placeholder={messages.shippingAddressPlaceholder}
                            required={isFieldMandatory(fieldSettings.shippingAddress)}
                            disabled={isFieldReadOnly(fieldSettings.shippingAddress)}
                            items={shippingAddressesItems}
                            itemNameKey='fullAddress'
                            size='small'
                            progress={isFetchingShippingAddresses}
                        />
                    )}
                </Grid>
            </Grid>
        </Form>
    );
});

InvoiceShippingDetailsSection.displayName = 'InvoiceShippingDetailsSection';
